import { Button as MuiButton, Tooltip, Typography, Box } from "@mui/material";
import {
  type ButtonColorOption,
  type ButtonVariantOption,
} from "@/components/Button";
import { type SxProps, type Theme } from "@mui/material/styles";
import { getHrefRel, getHrefTarget } from "@/utils/externalLink";
import { isInternalLink } from "@/utils/link";
import React from "react";
import { type SvgIconComponent } from "@mui/icons-material";
import { theme as appTheme } from "@/types/theme";
import { Link as RouterLink } from "react-router-dom";
import { useContactFormDialog } from "@/hooks/useContactFormDialog";

export interface MenuButtonProps {
  label: string;
  variant: ButtonVariantOption;
  color?: ButtonColorOption;
  disabled?: boolean;
  href?: string;
  description?: string;
  icon?: SvgIconComponent;
  actionType?: "contact-dialog";
}

export const MenuButton: React.FC<MenuButtonProps> = (props) => {
  const { openContactForm } = useContactFormDialog();

  const buttonSx: SxProps<Theme> = {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    padding: "16px 24px",
    gap: "8px",
    minHeight: "85px",
    height: "100%",
    width: "100%",
    backgroundColor: appTheme.palette.white.main,
    boxShadow: "0px 2px 8px -2px rgba(0, 0, 0, 0.25)",
    borderRadius: "8px",
    flex: "none",
    flexGrow: 0,
    "&:hover": {
      backgroundColor: appTheme.palette.lightgray.main,
    },
    "&.Mui-disabled": {
      backgroundColor: appTheme.palette.lightgray.main,
      boxShadow: "none",
    },
  };

  const buttonContent = (
    <Box
      display="flex"
      flexDirection="column"
      justifyContent="center"
      alignItems="center"
      gap={1}
    >
      {props.icon != null
        ? React.createElement(props.icon, {
            sx: {
              fontSize: "28px",
              color: props.disabled
                ? appTheme.palette.gray.main
                : appTheme.palette.primary.main,
            },
          })
        : null}
      <Typography
        sx={{
          fontSize: "15px",
          fontWeight: 500,
          color: props.disabled
            ? appTheme.palette.gray.main
            : appTheme.palette.black.main,
          textAlign: "center",
          whiteSpace: "pre-line",
        }}
      >
        {props.label}
      </Typography>
    </Box>
  );

  const wrapperStyle = {
    display: "block",
    width: "100%",
    height: "100%",
  };

  // お問い合わせダイアログの場合
  if (props.actionType === "contact-dialog") {
    return (
      <>
        <Tooltip title={props?.description ?? ""}>
          <span style={wrapperStyle}>
            <MuiButton
              variant={props.variant}
              color={props.color}
              disabled={props.disabled}
              onClick={openContactForm}
              sx={buttonSx}
            >
              {buttonContent}
            </MuiButton>
          </span>
        </Tooltip>
      </>
    );
  }

  // 内部リンクの場合はReact Routerを使用
  if (props.href && isInternalLink(props.href)) {
    return (
      <Tooltip title={props?.description ?? ""}>
        <span style={wrapperStyle}>
          <MuiButton
            component={RouterLink}
            to={props.href}
            variant={props.variant}
            color={props.color}
            disabled={props.disabled}
            sx={buttonSx}
          >
            {buttonContent}
          </MuiButton>
        </span>
      </Tooltip>
    );
  }

  // 外部リンクの場合
  if (props.href) {
    return (
      <Tooltip title={props?.description ?? ""}>
        <span style={wrapperStyle}>
          <MuiButton
            variant={props.variant}
            color={props.color}
            disabled={props.disabled}
            href={props.href}
            sx={buttonSx}
            target={getHrefTarget(props.href)}
            rel={getHrefRel(props.href)}
          >
            {buttonContent}
          </MuiButton>
        </span>
      </Tooltip>
    );
  }

  // hrefがない場合は通常のボタン
  return (
    <Tooltip title={props?.description ?? ""}>
      <span style={wrapperStyle}>
        <MuiButton
          variant={props.variant}
          color={props.color}
          disabled={props.disabled}
          sx={buttonSx}
        >
          {buttonContent}
        </MuiButton>
      </span>
    </Tooltip>
  );
};
